import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { CreditCard, Edit, Trash2, Download, Upload, Loader2, Search, FileSpreadsheet, AlertTriangle, RefreshCw } from 'lucide-react';
import { formatDateTime } from '../../lib/formatters';
import { BookLines } from '../../components/BooksFees';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ALL = '__all__';

const Payments = () => {
    const { user } = useAuth();
    const [payments, setPayments] = useState([]);
    const [parents, setParents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [editingPayment, setEditingPayment] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const [editForm, setEditForm] = useState({ amount: '', payment_type: '', method: '', receipt_number: '' });
    const [methodFilter, setMethodFilter] = useState(ALL);
    const [issuerFilter, setIssuerFilter] = useState(ALL);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const issuerOf = (p) => p.recorded_by_name || (p.method === 'stripe' ? 'Auto System' : '—');
    const localDay = (iso) => {
        const d = new Date(iso);
        if (isNaN(d)) return '';
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    const methodOptions = useMemo(() => Array.from(new Set(payments.map(p => p.method).filter(Boolean))).sort(), [payments]);
    const issuerOptions = useMemo(() => Array.from(new Set(payments.map(issuerOf))).sort(), [payments]);
    const clearFilters = () => { setMethodFilter(ALL); setIssuerFilter(ALL); setDateFrom(''); setDateTo(''); };

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [paymentsRes, parentsRes] = await Promise.all([
                axios.get(`${API_URL}/api/payments`, { withCredentials: true }),
                axios.get(`${API_URL}/api/parents`, { withCredentials: true })
            ]);
            setPayments(paymentsRes.data);
            setParents(parentsRes.data);
        } catch { toast.error('Failed to load data'); }
        finally { setLoading(false); }
    };

    const getParentName = (id) => { const p = parents.find(p => p.id === id); return p ? `${p.first_name} ${p.last_name}` : 'N/A'; };

    const handleEdit = (payment) => {
        setEditingPayment(payment);
        setEditForm({ amount: String(payment.amount), payment_type: payment.payment_type, method: payment.method, receipt_number: payment.receipt_number || '' });
        setEditDialogOpen(true);
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault(); setSaving(true);
        try {
            await axios.put(`${API_URL}/api/payments/${editingPayment.id}`, { ...editForm, amount: parseFloat(editForm.amount) }, { withCredentials: true });
            toast.success('Payment updated'); setEditDialogOpen(false); fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update'); }
        finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this payment receipt?')) return;
        try { await axios.delete(`${API_URL}/api/payments/${id}`, { withCredentials: true }); toast.success('Payment deleted'); fetchData(); }
        catch { toast.error('Failed to delete'); }
    };

    const handleDeleteAll = async () => {
        if (!window.confirm('DELETE ALL payment receipts? This cannot be undone!')) return;
        if (!window.confirm('Are you absolutely sure? All payment records will be permanently deleted.')) return;
        try { await axios.delete(`${API_URL}/api/payments`, { withCredentials: true }); toast.success('All payments deleted'); fetchData(); }
        catch { toast.error('Failed to delete'); }
    };

    const handleExportExcel = async () => {
        try {
            const response = await axios.get(`${API_URL}/api/payments/export/excel`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a'); link.href = url; link.setAttribute('download', 'payments.xlsx');
            document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
            toast.success('Payments exported');
        } catch { toast.error('Failed to export'); }
    };

    const [reconciling, setReconciling] = useState(false);
    const handleReconcile = async () => {
        setReconciling(true);
        try {
            const res = await axios.post(`${API_URL}/api/payments/reconcile-pending`, {}, { withCredentials: true });
            const { checked, finalized, skipped_unpaid_or_unknown } = res.data || {};
            if (finalized > 0) {
                toast.success(`Created ${finalized} new receipt${finalized > 1 ? 's' : ''} from Stripe (checked ${checked}). Refreshing…`);
                fetchData();
            } else {
                toast.info(`Checked ${checked} pending Stripe session${checked === 1 ? '' : 's'} — none paid yet (${skipped_unpaid_or_unknown || 0} unpaid/unknown).`);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Reconcile failed');
        } finally { setReconciling(false); }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0]; if (!file) return;
        if (!window.confirm('This will REPLACE all existing payment records with the uploaded data. Continue?')) { if (fileInputRef.current) fileInputRef.current.value = ''; return; }
        setUploading(true);
        const formData = new FormData(); formData.append('file', file);
        try {
            const response = await axios.post(`${API_URL}/api/payments/import/excel`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
            toast.success(`${response.data.imported} payment(s) imported`); setUploadDialogOpen(false); fetchData();
        } catch (error) { toast.error(error.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const filteredPayments = payments.filter(p => {
        const q = searchQuery.toLowerCase();
        if (q && !(p.receipt_number?.toLowerCase().includes(q) || getParentName(p.parent_id).toLowerCase().includes(q) || p.payment_type?.toLowerCase().includes(q))) return false;
        if (methodFilter !== ALL && (p.method || '') !== methodFilter) return false;
        if (issuerFilter !== ALL && issuerOf(p) !== issuerFilter) return false;
        if (dateFrom || dateTo) {
            const d = localDay(p.created_at);
            if (dateFrom && d < dateFrom) return false;
            if (dateTo && d > dateTo) return false;
        }
        return true;
    });
    const hasFilters = methodFilter !== ALL || issuerFilter !== ALL || dateFrom || dateTo;

    const totalAmount = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);

    if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div></div></DashboardLayout>;

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="admin-payments-page">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Payment Receipts</h1>
                        <p className="text-[#4B5563]">{payments.length} receipts | Total: {'\u20AC'}{totalAmount.toFixed(2)}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" onClick={handleReconcile} disabled={reconciling} data-testid="reconcile-stripe-btn">
                            {reconciling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                            {reconciling ? 'Checking…' : 'Reconcile Stripe'}
                        </Button>
                        <Button variant="outline" onClick={handleExportExcel} data-testid="export-payments-btn"><Download className="h-4 w-4 mr-2" />Export Excel</Button>
                        <Button variant="outline" onClick={() => setUploadDialogOpen(true)} data-testid="upload-payments-btn"><Upload className="h-4 w-4 mr-2" />Upload Excel</Button>
                        {user?.role === 'super_admin' && (
                            <Button variant="outline" className="text-[#EF4444] border-[#EF4444]" onClick={handleDeleteAll} data-testid="delete-all-payments-btn"><Trash2 className="h-4 w-4 mr-2" />Delete All</Button>
                        )}
                    </div>
                </div>

                <Card className="card-surface"><CardContent className="p-4 space-y-3">
                    <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" /><Input placeholder="Search by receipt, parent, type..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="payments-search" /></div>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <div className="space-y-1"><Label className="text-xs">Payment method</Label>
                            <Select value={methodFilter} onValueChange={setMethodFilter}>
                                <SelectTrigger data-testid="payments-method-filter"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value={ALL}>All methods</SelectItem>{methodOptions.map(m => <SelectItem key={m} value={m} className="capitalize">{m.replace('_', ' ')}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">Receipt issuer</Label>
                            <Select value={issuerFilter} onValueChange={setIssuerFilter}>
                                <SelectTrigger data-testid="payments-issuer-filter"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value={ALL}>All issuers</SelectItem>{issuerOptions.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-1"><Label className="text-xs">From date</Label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="payments-date-from" /></div>
                        <div className="space-y-1"><Label className="text-xs">To date</Label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="payments-date-to" /></div>
                        <Button variant="outline" onClick={clearFilters} disabled={!hasFilters} data-testid="payments-clear-filters">Clear filters</Button>
                    </div>
                    {hasFilters && <p className="text-xs text-[#064E3B]" data-testid="payments-filter-summary">{filteredPayments.length} receipts match · Total {'\u20AC'}{totalAmount.toFixed(2)}</p>}
                </CardContent></Card>

                <Card className="card-surface">
                    <CardContent className="p-0">
                        {filteredPayments.length > 0 ? (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader><TableRow><TableHead>Receipt #</TableHead><TableHead>Parent</TableHead><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Method</TableHead><TableHead>Issued by</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                                    <TableBody>
                                        {filteredPayments.map(p => (
                                            <TableRow key={p.id}>
                                                <TableCell className="font-mono text-sm">{p.receipt_number}</TableCell>
                                                <TableCell>{getParentName(p.parent_id)}</TableCell>
                                                <TableCell className="tabular-nums">{formatDateTime(p.created_at)}</TableCell>
                                                <TableCell className="capitalize">{p.payment_type === 'books' ? <Badge className="bg-[#B45309]">Books</Badge> : p.payment_type}<BookLines payment={p} /></TableCell>
                                                <TableCell className="capitalize">{(p.method || '').replace('_', ' ')}</TableCell>
                                                <TableCell className="text-sm text-[#4B5563]">{p.recorded_by_name || (p.method === 'stripe' ? 'Auto System' : '—')}</TableCell>
                                                <TableCell><Badge className={p.status === 'completed' ? 'bg-[#10B981]' : 'bg-[#F59E0B]'}>{p.status}</Badge></TableCell>
                                                <TableCell className="text-right font-semibold tabular-nums">{'\u20AC'}{p.amount?.toFixed(2)}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end space-x-1">
                                                        <Button size="sm" variant="ghost" onClick={() => handleEdit(p)}><Edit className="h-4 w-4" /></Button>
                                                        <Button size="sm" variant="ghost" className="text-[#EF4444]" onClick={() => handleDelete(p.id)}><Trash2 className="h-4 w-4" /></Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : <div className="flex flex-col items-center justify-center py-12"><CreditCard className="h-12 w-12 text-[#4B5563]/30 mb-4" /><p className="text-[#4B5563]">No payment receipts</p></div>}
                    </CardContent>
                </Card>

                {/* Edit Dialog */}
                <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-['Outfit']">Edit Payment</DialogTitle></DialogHeader>
                        <form onSubmit={handleEditSubmit} className="space-y-4">
                            <div className="space-y-2"><Label>Receipt Number</Label><Input value={editForm.receipt_number} onChange={(e) => setEditForm({...editForm, receipt_number: e.target.value})} /></div>
                            <div className="space-y-2"><Label>Amount ({'\u20AC'})</Label><Input type="number" step="0.01" value={editForm.amount} onChange={(e) => setEditForm({...editForm, amount: e.target.value})} required /></div>
                            <div className="space-y-2"><Label>Type</Label>
                                <Select value={editForm.payment_type} onValueChange={(v) => setEditForm({...editForm, payment_type: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="tuition">Tuition</SelectItem><SelectItem value="renewal">Renewal</SelectItem><SelectItem value="activity">Activity</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2"><Label>Method</Label>
                                <Select value={editForm.method} onValueChange={(v) => setEditForm({...editForm, method: v})}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="stripe">Stripe</SelectItem><SelectItem value="bank_transfer">Bank Transfer</SelectItem></SelectContent>
                                </Select>
                            </div>
                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                                <Button type="submit" disabled={saving} className="bg-[#064E3B] hover:bg-[#047857]">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Update</Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Upload Dialog */}
                <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
                    <DialogContent className="max-w-md">
                        <DialogHeader><DialogTitle className="font-['Outfit'] flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-[#064E3B]" /> Import Payments Excel</DialogTitle></DialogHeader>
                        <div className="space-y-4">
                            <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 text-sm text-[#DC2626] flex gap-2"><AlertTriangle className="h-5 w-5 flex-shrink-0" /><p>Uploading will <strong>replace all existing</strong> payment records with the data from the Excel file.</p></div>
                            <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg p-4 text-sm text-[#166534]"><p className="font-medium mb-1">Expected columns:</p><p>receipt_number, parent_id, amount, payment_type, method, status, created_at</p></div>
                            <div className="border-2 border-dashed border-[#D1D5DB] rounded-lg p-6 text-center">
                                <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" id="payments-file-upload" />
                                <label htmlFor="payments-file-upload" className="cursor-pointer">
                                    {uploading ? <div className="flex flex-col items-center"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B] mb-2" /><p className="text-sm">Uploading...</p></div> : <div className="flex flex-col items-center"><Upload className="h-8 w-8 text-[#4B5563] mb-2" /><p className="text-sm font-medium">Click to upload</p></div>}
                                </label>
                            </div>
                        </div>
                        <DialogFooter><Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Close</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </DashboardLayout>
    );
};

export default Payments;
